/* ═══════════════════════════════════════════════════════════════════════════
   video_fab.js  —  ambient floating video preview that resumes across pages

   Instagram/Reels-style behaviour: a small video card floats in the corner
   and autoplays muted immediately, playing through once — no click
   required, no modal, nothing blocks the rest of the page. Tap the card
   to pause/resume, tap the sound icon to toggle mute, tap the × to
   dismiss it for the rest of the browsing session.

   This is a traditional multi-page Frappe site (full page reload per
   route), not a single-page app, so the widget can't literally keep
   playing while the browser navigates. Instead it persists *state*:
   playback position and mute preference are saved to localStorage on an
   interval and on pagehide, then restored when the widget remounts on the
   next page — so it resumes from where it left off rather than restarting
   from zero every time, which is what "persists while navigating" can
   actually mean across full page loads.

   Architecture mirrors whatsapp_widget.js: loads via web_include_js, reads
   its config from a <head> schema and self-mounts with zero markup changes
   to any page template. Stacks above the WhatsApp button by default when
   both sit on the same corner.

   SCHEMA (set in <head> via Website HQ Settings -> Video Lightbox):
     window.VideoFabSchema = {
       enabled:  true,
       url:      'https://youtu.be/xxxxxxxxxxx',   // YouTube or Vimeo
       label:    'Watch Video',
       position: 'Bottom Right' | 'Bottom Left',
     }

   OPT-OUT:
     data-no-video-fab on <body>     skip entire widget for this page
   ═══════════════════════════════════════════════════════════════════════════ */

(function () {
  'use strict';

  var schema = window.VideoFabSchema || {};
  if (!schema.enabled || !schema.url) return;

  var SKIP_PATHS = [
    '/login', '/register', '/update-password', '/complete-signup',
    '/forgot', '/verify', '/qrcode', '/printview', '/printpreview',
  ];

  var DISMISS_KEY  = 'rfhq-video-fab-dismissed';
  var POSITION_KEY = 'rfhq-video-fab-position';
  var MUTE_KEY     = 'rfhq-video-fab-muted';

  var MUTE_ICON   = '<svg viewBox="0 0 24 24" width="14" height="14" fill="#fff"><path d="M16.5 12A4.5 4.5 0 0 0 14 8v2.18l2.45 2.45c.03-.2.05-.42.05-.63zm2.5 0c0 .94-.2 1.82-.54 2.64l1.51 1.51A8.91 8.91 0 0 0 21 12c0-4.28-2.99-7.86-7-8.77v2.06c2.89.86 5 3.54 5 6.71zM4.27 3 3 4.27 7.73 9H3v6h4l5 5v-6.73l4.25 4.25c-.67.52-1.42.93-2.25 1.18v2.06a8.99 8.99 0 0 0 3.69-1.81L19.73 21 21 19.73l-9-9L4.27 3zM12 4 9.91 6.09 12 8.18V4z"/></svg>';
  var UNMUTE_ICON = '<svg viewBox="0 0 24 24" width="14" height="14" fill="#fff"><path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3A4.5 4.5 0 0 0 14 8v8a4.5 4.5 0 0 0 2.5-4zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z"/></svg>';

  function parseYoutubeId(url) {
    try {
      var u = new URL(url);
      if (u.hostname.indexOf('youtu.be') !== -1) return u.pathname.slice(1).split('?')[0];
      return u.searchParams.get('v');
    } catch (_) { return null; }
  }

  function parseVimeoId(url) {
    var m = url.match(/vimeo\.com\/(\d+)/);
    return m ? m[1] : null;
  }

  var ytId = parseYoutubeId(schema.url);
  var vmId = !ytId && parseVimeoId(schema.url);

  /* ── Persisted state ───────────────────────────────────────────────────── */

  function readPosition() {
    try {
      var saved = JSON.parse(localStorage.getItem(POSITION_KEY) || 'null');
      return (saved && saved.url === schema.url) ? (saved.time || 0) : 0;
    } catch (_) { return 0; }
  }

  function savePosition(time) {
    if (!time) return;
    try {
      localStorage.setItem(POSITION_KEY, JSON.stringify({ url: schema.url, time: time }));
    } catch (_) {}
  }

  function readMuted() {
    try {
      var v = localStorage.getItem(MUTE_KEY);
      return v === null ? true : v === '1';
    } catch (_) { return true; }
  }

  function saveMuted(muted) {
    try { localStorage.setItem(MUTE_KEY, muted ? '1' : '0'); } catch (_) {}
  }

  function loadScript(src, onload) {
    var s = document.createElement('script');
    s.src = src;
    if (onload) s.onload = onload;
    document.head.appendChild(s);
  }

  /* ── Shared card chrome (sound + close buttons) ──────────────────────────
     `api` = { isMuted(), setMuted(bool), getCurrentTime(), destroy() }      */

  function bindControls(card, api) {
    var soundBtn = card.querySelector('.video-fab-sound');
    var closeBtn = card.querySelector('.video-fab-close');

    function refresh(muted) {
      soundBtn.innerHTML = muted ? MUTE_ICON : UNMUTE_ICON;
      soundBtn.setAttribute('aria-label', muted ? 'Unmute video' : 'Mute video');
      soundBtn.setAttribute('aria-pressed', String(!muted));
    }
    refresh(api.isMuted());

    soundBtn.addEventListener('click', function (e) {
      e.stopPropagation();
      var next = !api.isMuted();
      api.setMuted(next);
      saveMuted(next);
      refresh(next);
    });

    var flush = function () { savePosition(api.getCurrentTime()); };

    closeBtn.addEventListener('click', function (e) {
      e.stopPropagation();
      flush();
      api.destroy();
      card.remove();
      try { sessionStorage.setItem(DISMISS_KEY, '1'); } catch (_) {}
    });

    /* Position is flushed on the events that can lose it (pause, dismiss,
       pagehide, tab switch) — no polling interval needed. */
    card.addEventListener('click', function () {
      api.togglePlay();
      flush();
    });

    window.addEventListener('pagehide', flush);
    document.addEventListener('visibilitychange', function () {
      if (document.visibilityState === 'hidden') flush();
    });
  }

  /* ── YouTube ──────────────────────────────────────────────────────────── */

  function mountYoutube(playerEl, card) {
    var startAt = Math.floor(readPosition());
    var muted   = readMuted();

    function create() {
      var player = new YT.Player(playerEl, {
        videoId: ytId,
        playerVars: {
          autoplay: 1, mute: muted ? 1 : 0,
          controls: 0, modestbranding: 1, rel: 0, playsinline: 1,
          showinfo: 0, start: startAt,
        },
        events: {
          onReady: function () {
            bindControls(card, {
              isMuted:        function ()  { try { return player.isMuted(); } catch (_) { return muted; } },
              setMuted:       function (m) { try { m ? player.mute() : player.unMute(); } catch (_) {} },
              getCurrentTime: function ()  { try { return player.getCurrentTime(); } catch (_) { return 0; } },
              togglePlay:     function ()  {
                try {
                  var state = player.getPlayerState();
                  if (state === YT.PlayerState.PLAYING) player.pauseVideo();
                  else player.playVideo();
                } catch (_) {}
              },
              destroy:        function ()  { try { player.destroy(); } catch (_) {} },
            });
          },
        },
      });
    }

    if (window.YT && window.YT.Player) {
      create();
    } else {
      var prevReady = window.onYouTubeIframeAPIReady;
      window.onYouTubeIframeAPIReady = function () {
        if (prevReady) prevReady();
        create();
      };
      if (!document.querySelector('script[src*="youtube.com/iframe_api"]')) {
        loadScript('https://www.youtube.com/iframe_api');
      }
    }
  }

  /* ── Vimeo ────────────────────────────────────────────────────────────── */

  function mountVimeo(playerEl, card) {
    var startAt = readPosition();
    var muted   = readMuted();

    function create() {
      var player = new Vimeo.Player(playerEl, {
        id: vmId, autoplay: true, muted: muted, loop: false,
        controls: false, dnt: true, background: false,
      });

      var ready = player.ready();
      if (startAt) ready = ready.then(function () { return player.setCurrentTime(startAt); }).catch(function () {});
      ready.then(function () {
        bindControls(card, {
          isMuted:        function ()  { return muted; },
          setMuted:       function (m) { muted = m; player.setMuted(m).catch(function () {}); },
          getCurrentTime: function ()  { return lastTime; },
          togglePlay:     function ()  {
            player.getPaused().then(function (paused) {
              if (paused) player.play(); else player.pause();
            }).catch(function () {});
          },
          destroy:        function ()  { try { player.destroy(); } catch (_) {} },
        });
      });

      var lastTime = startAt;
      player.on('timeupdate', function (data) { lastTime = data.seconds; });
    }

    if (window.Vimeo && window.Vimeo.Player) {
      create();
    } else {
      loadScript('https://player.vimeo.com/api/player.js', create);
    }
  }

  /* ── Mount ────────────────────────────────────────────────────────────── */
  /*
     The card mounts as a lightweight facade — poster image + play glyph,
     zero third-party code — and only loads the YouTube/Vimeo iframe API
     when the visitor actually clicks it. The old behaviour (autoplaying
     iframe + player API on every single page) made every visitor pay the
     third-party JS, iframe and video-stream cost during initial page
     interaction whether they ever watched or not.
  */

  function mount() {
    var path = window.location.pathname.replace(/\/$/, '');
    if (SKIP_PATHS.indexOf(path) !== -1) return;
    if (document.body.hasAttribute('data-no-video-fab')) return;
    if (document.querySelector('.video-fab')) return;
    if (!ytId && !vmId) return; // unsupported URL — fail quietly, no broken widget
    try {
      if (sessionStorage.getItem(DISMISS_KEY) === '1') return;
    } catch (_) {}

    var posClass = schema.position === 'Bottom Left' ? 'video-fab--left' : 'video-fab--right';

    var poster = ytId
      ? '<img class="video-fab-poster" src="https://i.ytimg.com/vi/' + ytId + '/hqdefault.jpg" alt="" loading="lazy" decoding="async">'
      : '';  // Vimeo posters need an API call — the dark card + play glyph is enough

    var card = document.createElement('div');
    card.className = 'video-fab ' + posClass;
    card.setAttribute('role', 'complementary');
    card.setAttribute('aria-label', schema.label || 'Promotional video');
    card.innerHTML =
      '<div class="video-fab-frame"><div class="video-fab-player">' + poster +
        '<span class="video-fab-play" aria-hidden="true"></span>' +
      '</div></div>' +
      '<button type="button" class="video-fab-sound" aria-label="Unmute video" aria-pressed="false" hidden>' + MUTE_ICON + '</button>' +
      '<button type="button" class="video-fab-close" aria-label="Dismiss video">&times;</button>';
    document.body.appendChild(card);

    /* Facade-phase dismiss. After activation bindControls adds the full
       close handler (destroy + remove + dismiss) on the same button; both
       running together is harmless — every step is idempotent. */
    card.querySelector('.video-fab-close').addEventListener('click', function (e) {
      e.stopPropagation();
      card.remove();
      try { sessionStorage.setItem(DISMISS_KEY, '1'); } catch (_) {}
    });

    card.addEventListener('click', function activate(e) {
      if (e.target.closest('.video-fab-close')) return;
      card.removeEventListener('click', activate);
      var playerEl = card.querySelector('.video-fab-player');
      playerEl.innerHTML = '';  // drop the facade before the embed takes over
      card.querySelector('.video-fab-sound').hidden = false;
      if (ytId) mountYoutube(playerEl, card);
      else mountVimeo(playerEl, card);
    });
  }

  function scheduleMount() {
    /* Even the facade can wait until the main thread is free — nothing
       above-the-fold depends on it. */
    if ('requestIdleCallback' in window) {
      requestIdleCallback(mount, { timeout: 4000 });
    } else {
      setTimeout(mount, 2000);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scheduleMount);
  } else {
    scheduleMount();
  }

}());
